import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard, Shield, RefreshCw, CheckCircle, AlertCircle, Loader2, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getAccessToken } from '../../../api/client';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface RateInfo {
    tauxChange: number;
    montantHTG: number;
    montantBrutUSD: number;
    montantNetUSD: number;
    fraisStripe: number;
}

const StripePayment: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { t } = useTranslation();

    // Get params from navigation state
    const state = location.state as {
        amount: string;
        accountCode: string;
        method: string;
    } | null;

    const amount = state?.amount || '0';
    const accountCode = state?.accountCode || '';

    const [loading, setLoading] = useState(true);
    const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [rateInfo, setRateInfo] = useState<RateInfo>({
        tauxChange: 130,
        montantHTG: 0,
        montantBrutUSD: 0,
        montantNetUSD: 0,
        fraisStripe: 0
    });

    useEffect(() => {
        const fetchSessionDetails = async (sessionId: string) => {
            try {
                const token = getAccessToken();
                const response = await fetch(`${API_BASE_URL}/api/transactions/stripe/session-status/?session_id=${sessionId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    const metadata = data.metadata || {};

                    setRateInfo({
                        tauxChange: parseFloat(metadata.taux_change || '130'),
                        montantHTG: parseFloat(metadata.montant_htg || '0'),
                        montantBrutUSD: parseFloat(metadata.montant_brut_usd || '0'),
                        montantNetUSD: parseFloat(metadata.montant_net_usd || '0'),
                        fraisStripe: parseFloat(metadata.frais_stripe || '0')
                    });

                    // Show success after fetching data
                    setShowSuccess(true);
                    setLoading(false);

                    // Redirect after 4 seconds
                    setTimeout(() => {
                        navigate('/home', { replace: true });
                    }, 5000);
                } else {
                    // Fallback if fetch fails (e.g. invalid session)
                    setLoading(false);
                    setError(t('dashboard:errors.fetchError'));
                }
            } catch (e) {
                console.error("Error fetching session:", e);
                setLoading(false);
            }
        };

        // Check for success/cancel URL params
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');

        if (params.get('success') === 'true' && sessionId) {
            // Fetch real details from backend
            fetchSessionDetails(sessionId);
            return;
        } else if (params.get('success') === 'true') {
            // Legacy fallback if no session_id (should not happen with new backend)
            setShowSuccess(true);
            setLoading(false);
            setTimeout(() => navigate('/home'), 4000);
            return;
        }

        if (params.get('canceled') === 'true') {
            setError('CANCELED');
            setLoading(false);
            return;
        }

        // Only create session if we have the required params and NO success params
        if (amount && accountCode && !sessionId && !params.get('success')) {
            createStripeSession();
        } else if (!sessionId && !params.get('success')) {
            setError(t('services:payment.error'));
            setLoading(false);
        }
    }, []);

    const createStripeSession = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = getAccessToken();
            if (!token) {
                setError(t('dashboard:errors.noAuthToken'));
                setLoading(false);
                return;
            }

            // Call Stripe create-checkout API
            const response = await fetch(`${API_BASE_URL}/api/transactions/stripe/create-checkout/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    compte: accountCode,
                    montant: parseFloat(amount),
                    description: 'Dépôt Ti Kanè Bousanm via Stripe',
                    // Add success/cancel URLs for web
                    success_url: `${window.location.origin}/services/payment/stripe?success=true`,
                    cancel_url: `${window.location.origin}/services/payment/stripe?canceled=true`,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.detail || t('services:depot.error'));
            }

            if (data.checkout_url) {
                setCheckoutUrl(data.checkout_url);
                setRateInfo({
                    tauxChange: data.taux_change || 130,
                    montantHTG: data.montant_htg || 0,
                    montantBrutUSD: data.montant_brut_usd || parseFloat(amount),
                    montantNetUSD: data.montant_net_usd || parseFloat(amount),
                    fraisStripe: data.frais_stripe || 0
                });

                // Redirect immediately
                window.location.href = data.checkout_url;
            } else {
                throw new Error(t('services:payment.error'));
            }
        } catch (err: any) {
            console.error('Stripe session error:', err);
            setError(err.message || 'Yon erè rive');
        } finally {
            // Keep loading true if redirecting
        }
    };

    const handleRetry = () => {
        setError(null);
        createStripeSession();
    };

    const handleClose = () => {
        if (window.confirm(t('services:payment.confirmClose.message'))) {
            navigate(-1);
        }
    };

    const formatNumber = (num: number) => {
        return num.toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-600 to-purple-700">
                {/* Header */}
                <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-4 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                    <div className="flex items-center gap-2">
                        <CreditCard className="w-6 h-6 text-white" />
                        <span className="text-lg font-bold text-white">Stripe</span>
                    </div>
                    <div className="w-10" />
                </div>

                <div className="flex flex-col items-center justify-center flex-1 min-h-[60vh] p-8">
                    <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
                    <p className="text-white text-lg font-medium">{t('services:payment.preparing')}</p>
                    <p className="text-white/70 text-sm mt-2">{t('services:detailsPage.amount')}: ${parseFloat(amount).toFixed(2)} USD</p>
                </div>
            </div>
        );
    }

    // Cancelled State
    if (error === 'CANCELED') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center p-4">
                <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-8 max-w-sm w-full shadow-lg border border-white/50 text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-orange-500" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-800 mb-2">{t('services:payment.cancelled.title')}</h2>
                    <p className="text-slate-500 mb-6">{t('services:payment.cancelled.message')}</p>

                    <div className="flex flex-col gap-3">
                        <button
                            onClick={handleRetry}
                            className="bg-violet-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-violet-700 transition-colors flex items-center justify-center gap-2"
                        >
                            <RefreshCw className="w-5 h-5" />
                            {t('services:payment.retry')}
                        </button>
                        <button
                            onClick={() => navigate(-1)}
                            className="text-slate-500 font-medium hover:bg-slate-50 py-3 rounded-xl transition-colors"
                        >
                            {t('services:payment.back')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Generic Error State
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50">
                {/* Header */}
                <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-4 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"
                    >
                        <ArrowLeft className="w-5 h-5 text-white" />
                    </button>
                    <div className="flex items-center gap-2">
                        <CreditCard className="w-6 h-6 text-white" />
                        <span className="text-lg font-bold text-white">Stripe</span>
                    </div>
                    <div className="w-10" />
                </div>

                <div className="flex flex-col items-center justify-center flex-1 min-h-[60vh] p-8">
                    <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                    <h2 className="text-xl font-bold text-red-500 mb-2">{t('services:payment.error')}</h2>
                    <p className="text-slate-600 text-center mb-6">{error}</p>
                    <button
                        onClick={handleRetry}
                        className="flex items-center gap-2 bg-violet-600 text-white px-6 py-3 rounded-xl font-bold"
                    >
                        <RefreshCw className="w-5 h-5" />
                        {t('services:payment.retry')}
                    </button>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-4 text-slate-500"
                    >
                        {t('services:payment.back')}
                    </button>
                </div>
            </div>
        );
    }

    // Success State
    if (showSuccess) {
        const amountBrutUSD = rateInfo.montantBrutUSD || parseFloat(amount);
        const stripeFee = rateInfo.fraisStripe || 0;
        const exchangeRate = rateInfo.tauxChange;
        const amountHTG = rateInfo.montantHTG || (rateInfo.montantNetUSD * exchangeRate);

        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center p-4">
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full shadow-xl border border-white/50">
                    {/* Success Icon */}
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-12 h-12 text-green-500" />
                    </div>

                    <h2 className="text-2xl font-bold text-green-500 text-center mb-1">{t('services:payment.success.title')}</h2>
                    <p className="text-slate-500 text-center mb-6">{t('services:payment.success.message')}</p>

                    {/* Transaction Details */}
                    <div className="bg-slate-50 rounded-2xl p-4 mb-4">
                        <div className="flex justify-between py-2">
                            <span className="text-slate-500">{t('services:payment.amountPaid')}</span>
                            <span className="font-medium">${formatNumber(amountBrutUSD)} USD</span>
                        </div>
                        <div className="flex justify-between py-2">
                            <span className="text-slate-500">{t('services:payment.stripeFee')}</span>
                            <span className="font-medium text-red-500">~${formatNumber(stripeFee)}</span>
                        </div>
                        <div className="border-t border-slate-200 my-2" />
                        <div className="flex justify-between py-2">
                            <span className="text-slate-500">{t('services:payment.exchangeRate')}</span>
                            <span className="font-medium">1 USD = {exchangeRate} HTG</span>
                        </div>
                        <div className="flex justify-between py-3 bg-violet-50 rounded-xl px-3 mt-2">
                            <span className="font-bold text-violet-600">{t('services:payment.creditedAmount')}</span>
                            <span className="font-bold text-violet-600">{formatNumber(amountHTG)} HTG</span>
                        </div>
                    </div>

                    {/* Account Info */}
                    <div className="flex items-center justify-center gap-2 text-violet-600 mb-4">
                        <CreditCard className="w-4 h-4" />
                        <span className="text-sm font-medium">{t('services:detailsPage.accountNumber')}: {accountCode}</span>
                    </div>

                    {/* Redirect Indicator */}
                    <div className="flex items-center justify-center gap-2 text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">{t('services:payment.returnToHome')}</span>
                    </div>
                </div>
            </div>
        );
    }

    // Checkout URL Ready - Redirect to Stripe
    if (checkoutUrl) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-600 to-purple-700 flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-4 flex items-center justify-between">
                    <button
                        onClick={handleClose}
                        className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                    <div className="flex items-center gap-2">
                        <CreditCard className="w-6 h-6 text-white" />
                        <span className="text-lg font-bold text-white">{t('services:payment.secure', { provider: 'Stripe' })}</span>
                    </div>
                    <div className="bg-white/20 px-3 py-1.5 rounded-lg">
                        <span className="text-white font-bold">${parseFloat(amount).toFixed(2)}</span>
                    </div>
                </div>

                {/* Redirecting / Manual Pay Button */}
                <div className="flex-1 bg-white flex items-center justify-center flex-col p-8">
                    <div className="bg-violet-50 p-4 rounded-full mb-6">
                        <CreditCard className="w-12 h-12 text-violet-600" />
                    </div>

                    <h3 className="text-xl font-bold text-slate-800 mb-2">{t('services:payment.ready.title')}</h3>
                    <p className="text-slate-500 text-center max-w-md mb-8">
                        {t('services:payment.ready.message')}
                    </p>

                    <button
                        onClick={() => window.location.href = checkoutUrl}
                        className="bg-violet-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-violet-200 hover:bg-violet-700 transition-all flex items-center gap-2 transform hover:scale-105 active:scale-95"
                    >
                        <span>{t('services:payment.payNow')}</span>
                        <ArrowLeft className="w-5 h-5 rotate-180" />
                    </button>

                    <p className="text-xs text-slate-400 mt-6 text-center">
                        {t('services:payment.automaticRedirectFallback')}
                    </p>
                </div>

                {/* Footer */}
                <div className="bg-white/95 backdrop-blur-xl border-t border-slate-200 py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                        <Shield className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-slate-500">{t('services:payment.secure', { provider: 'Stripe' })}</span>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default StripePayment;
